import { ModalRef, useTranslation } from '@msdk/common'
import {
  CustomActionPosition,
  Entity,
  FormMode,
  FormViewDefinition,
  FormViewDefinitionHideColumnHeader,
  FormViewDefinitionHideDescription,
  FormViewDefinitionHideHeader,
  FormViewDefinitionHidePagination,
  ModalForm,
  RowClickModeDefiniton,
} from '@msdk/definitions'
import {
  Card as BaseCard,
  CardActions,
  CardBody as BaseCardBody,
  createComponentWrapperWithRef,
  EmbeddedTableWrapper,
  Pagination as BasePagination,
  styled,
  useComponentLibrary,
} from '@msdk/ui'
import React, { useEffect } from 'react'

import { useEmbeddedView, useFormModal, useViewActionButtons } from '../../hooks'
import { filtersToObject } from '../../utils'
import { DynamicView } from '../DynamicView'
import { DynamicViewProvider } from '../DynamicView/context'
import { EmptyStateOverlayVisualization } from '../DynamicView/EmptyStateOverlay'
import { ModalFormDynamicCloseArgs } from '../ModalForm/ModalFormDynamic'
import { ViewHeaderLinks } from '../ViewHeaderLinks'
import { EmbeddedViewHeader } from './EmbeddedViewHeader'

const MarginBottomCard = styled(BaseCard)`
  margin-bottom: 16px;
`

const MuiCard = styled(MarginBottomCard)`
  border-radius: 8px;
`

const NovaCard = styled(MarginBottomCard)``

const Card = createComponentWrapperWithRef(
  {
    mui: MuiCard,
    nova: NovaCard,
    muiprops: {
      variant: 'outlined',
    },
  },
  'Card',
)

const PaginationWrapper = styled('div')`
  display: flex;
`

const MuiPagination = styled(BasePagination)`
  margin-left: auto;
  margin-right: 16px;
`

const NovaPagination = styled(BasePagination)`
  margin-left: auto;
  border: none;
`

const Pagination = createComponentWrapperWithRef(
  {
    mui: MuiPagination,
    nova: NovaPagination,
  },
  'Pagination',
)

const MuiCardBody = styled(BaseCardBody)`
  && {
    padding: 0;
  }
`

const NovaCardBody = styled(BaseCardBody)``

const CardBody = createComponentWrapperWithRef(
  {
    mui: MuiCardBody,
    nova: NovaCardBody,
  },
  'CardBody',
)

interface Props {
  entityName: string
  viewName: string
  containerEntity: Entity
  viewDef: FormViewDefinition
  modalRef?: ModalRef
  refreshVersion?: number
  refreshView?: (viewName: string) => void
  onDataChanged?: () => void
}

export function EmbeddedView(props: Props) {
  const t = useTranslation()
  const { createModal: createFormModal } = useFormModal()

  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    onPageChange,
    fetchData,
    onOrderChange,
    reload,
    currentFilters,
    initialFilters,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    currentOrder,
    isInitialized,
    addOrUpdateFilter,
    filterChange,
    clearAllFilters,
    isMultiselected,
    setIsMultiselect,
  } = useEmbeddedView(
    props.entityName,
    props.viewName,
    props.containerEntity,
    filtersToObject(props.viewDef.viewFilterFields, props.containerEntity),
    props.viewDef.pageSize,
  )

  useEffect(() => {
    if (!props.refreshVersion) {
      return
    }

    fetchData()
  }, [props.refreshVersion])

  const notifyDataChanged = () => {
    if (props.onDataChanged) {
      props.onDataChanged()
      return
    }

    fetchData()
  }

  const { showFilters } = props.viewDef

  const onCreate = () => {
    const modalRef = createFormModal(
      entityModel.name,
      entityModel.createForm ?? entityModel.defaultForm,
      'Create',
      (entity: Entity) => {
        if (!entity) {
          return
        }
        notifyDataChanged()
      },
      {
        mode: 'Edit',
        modalForm: viewModel.modalForm,
        containerEntity: props.containerEntity,
      },
    )
    modalRef.open()
  }

  const openModalForm = (
    entityName: string,
    formName: string,
    id: string,
    mode: FormMode,
    modalForm?: ModalForm,
    containerEntity?: Entity,
  ) => {
    const modalRef = createFormModal(
      entityName,
      formName,
      id,
      (args: ModalFormDynamicCloseArgs) => {
        if (args && args.newFormRequired) {
          openModalForm(entityName, args.newFormRequired, id, args.mode, modalForm, containerEntity)
        } else {
          notifyDataChanged()
        }
      },
      { mode, modalForm, containerEntity },
    )
    modalRef.open()
  }

  const onRowClicked = (row: Entity) => {
    openModalForm(
      entityModel.name,
      entityModel.defaultForm,
      row.id,
      'Readonly',
      viewModel.modalForm,
      props.containerEntity,
    )
  }

  const onRowClickedFactory = () => (props.viewDef.canEdit ? onRowClicked : undefined)

  const customActions = useViewActionButtons(viewModel, CustomActionPosition.InsideView, reload, undefined, acl)

  const shouldShowHeader =
    props.viewDef.hideHeader == FormViewDefinitionHideHeader.False ||
    (props.viewDef.hideHeader === FormViewDefinitionHideHeader.Undefined && !viewModel.hideHeader)
  const shouldShowDescription =
    props.viewDef.hideDescription === FormViewDefinitionHideDescription.False ||
    (props.viewDef.hideDescription === FormViewDefinitionHideDescription.Undefined && !viewModel.hideDescription)
  const shouldShowColumnHeader =
    props.viewDef.hideColumnHeader === FormViewDefinitionHideColumnHeader.False ||
    (props.viewDef.hideColumnHeader === FormViewDefinitionHideColumnHeader.Undefined && !viewModel.hideColumnHeader)
  const shouldShowPagination =
    props.viewDef.hidePagination === FormViewDefinitionHidePagination.False ||
    (props.viewDef.hidePagination === FormViewDefinitionHidePagination.Undefined && !viewModel.hidePagination)

  const areRowsClickable =
    props.viewDef.rowClickMode === RowClickModeDefiniton.Clickable ||
    (props.viewDef.rowClickMode === RowClickModeDefiniton.Undefined &&
      viewModel.rowClickMode !== RowClickModeDefiniton.Unclickable)

  const lib = useComponentLibrary()

  const rows = response?.source || []

  const canBeCreated = entityModel.canBeCreated && viewModel.canBeCreated && props.viewDef.canCreate && acl.canCreate
  const description = t(viewModel.idml, viewModel.description)

  return (
    <>
      {viewModel && (
        <Card>
          {shouldShowHeader && (
            <EmbeddedViewHeader
              canBeCreated={canBeCreated}
              onCreate={onCreate}
              isInitialized={isInitialized}
              customActions={customActions}
              entityName={viewModel.entity}
              viewName={viewModel.name}
              reload={reload}
              currentFilters={currentFilters}
              acl={acl}
              shouldShowDescription={shouldShowDescription}
              description={description}
              showFilters={showFilters}
              initialFilters={initialFilters}
              filters={viewModel.filters}
              entityModel={entityModel}
              isMultiselected={isMultiselected}
              filterChange={filterChange}
              setIsMultiselect={setIsMultiselect}
              headerLinks={viewModel.headerLinks}
            />
          )}
          <CardBody>
            <EmbeddedTableWrapper hideHeader={!shouldShowColumnHeader} hidePaginator={!shouldShowPagination}>
              <DynamicViewProvider
                entityModel={entityModel}
                viewModel={viewModel}
                reload={reload}
                rows={rows}
                isLoading={isLoading}
                onRowClick={onRowClickedFactory()}
                onOrderChange={onOrderChange}
                hideHeader={!shouldShowColumnHeader}
                acl={acl}
                modalRef={props.modalRef}
                currentFilters={currentFilters}
                additionalCondition={additionalCondition}
                addColumnFilter={addColumnFilter}
                removeColumnFilter={removeColumnFilter}
                removeAllColumnFilter={removeAllColumnFilter}
                currentOrder={currentOrder}
                styleMode="Compact"
                behaviour={{
                  emptyState: EmptyStateOverlayVisualization.Text,
                }}
                areRowsClickable={areRowsClickable}
                addOrUpdateFilter={addOrUpdateFilter}
                isEmbedded
                onDataChanged={props.onDataChanged}
                refreshView={props.refreshView}
                clearAllFilters={clearAllFilters}
              >
                <DynamicView />
              </DynamicViewProvider>
            </EmbeddedTableWrapper>
            {shouldShowPagination && (
              <PaginationWrapper>
                <Pagination
                  count={response?.totalItems ?? 0}
                  page={currentPage}
                  rowsPerPage={response?.pageSize ?? 30}
                  rowsPerPageOptions={[]}
                  onPageChange={(_e, page) => onPageChange(page)}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('CMPSR_PAGINATOR_OF', 'of')} ${count}`
                  }
                />
              </PaginationWrapper>
            )}
          </CardBody>
          {lib === 'nova' && rows.length > 0 && (
            <CardActions>
              <ViewHeaderLinks links={viewModel.headerLinks} reload={reload} />
            </CardActions>
          )}
        </Card>
      )}
    </>
  )
}
